import { ReactNode, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
  IconButton,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PsychologyIcon from '@mui/icons-material/Psychology';
import type { ScopeBlock, ToolCallBlock, MessageBlock, ErrorBlock, TodoItem } from '@trinity/chat';
import type { ReasoningNode } from './interfaces';
import { ChatMarkdown } from '../Markdown/Markdown';

const BlockShell = styled(Paper)<{ depth?: number }>(({ theme, depth = 0 }) => ({
  padding: theme.spacing(1.5),
  marginLeft: depth * 16,
  borderRadius: 10,
}));

const Row = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const Grow = styled(Box)`
  flex: 1 1 auto;
  min-width: 0;
`;

const Pre = styled('pre')(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1),
  overflowX: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  // background: theme.palette.action.hover,
  borderRadius: 8,
  fontFamily: theme.typography.fontFamily,
  fontSize: 13,
}));

const ChildStack = styled(Stack)`
  margin-top: 12px;
  gap: 12px;
`;

const shimmerPulse = keyframes`
  0%   { opacity: 1; }
  50%  { opacity: 0.3; }
  100% { opacity: 1; }
`;

const Shimmer = styled(Box)`
  animation: ${shimmerPulse} 1.4s ease-in-out infinite;
`;

const SectionPaper = styled(Paper)`
  padding: 12px;
  border-radius: 12px;
`;

function getStatusColor(
  status: string
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
  switch (status) {
    case 'completed':
    case 'done':
      return 'success';
    case 'running':
    case 'streaming':
    case 'thinking':
    case 'running_tool':
    case 'in_progress':
      return 'info';
    case 'pending':
    case 'idle':
      return 'default';
    case 'error':
      return 'error';
    default:
      return 'default';
  }
}
function formatJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function formatDuration(ms: number): string {
  const seconds = Math.round(ms / 1000);
  return seconds === 1 ? '1 second' : `${seconds} seconds`;
}

export function ReasoningBlockView({ node }: { node: ReasoningNode }) {
  const [open, setOpen] = useState(false);
  const hasDetails = !!node.detailText?.trim();
  const isComplete = node.status === 'completed' && !node.streaming;

  const label = isComplete
    ? node.durationMs !== undefined
      ? `Thought for ${formatDuration(node.durationMs)}`
      : 'Thought'
    : (node.summary ?? 'Thinking');

  return (
    <BlockShell variant="outlined" depth={node.depth}>
      <Stack spacing={1}>
        <Row>
          {hasDetails ? (
            <IconButton size="small" onClick={() => setOpen((v) => !v)} aria-label="toggle reasoning details">
              {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ width: 32 }} />
          )}

          {node.streaming ? (
            <Shimmer sx={{ display: 'flex', alignItems: 'center' }}>
              <PsychologyIcon fontSize="small" color="action" />
            </Shimmer>
          ) : null}

          <Grow>
            {node.streaming ? (
              <Shimmer>
                <Typography variant="body2" fontWeight={600}>
                  {label}
                </Typography>
              </Shimmer>
            ) : (
              <Typography variant="body2" fontWeight={600}>
                {label}
              </Typography>
            )}
          </Grow>
        </Row>

        {hasDetails ? (
          <Collapse in={open}>
            <Pre>{node.detailText}</Pre>
          </Collapse>
        ) : null}
      </Stack>
    </BlockShell>
  );
}

export function ScopeBlockView({ block, children }: { block: ScopeBlock; children?: ReactNode }) {
  const [open, setOpen] = useState(true);

  return (
    <BlockShell variant="outlined" depth={block.depth}>
      <Stack spacing={1}>
        <Row>
          <IconButton size="small" onClick={() => setOpen((v) => !v)} aria-label="toggle scope">
            {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>

          <Grow>
            <Typography variant="body2" fontWeight={600}>
              {block.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {block.scopeKind}
            </Typography>
          </Grow>

          <Chip size="small" label={block.status} color={getStatusColor(block.status)} />
        </Row>

        <Collapse in={open}>
          <ChildStack>{children}</ChildStack>
        </Collapse>
      </Stack>
    </BlockShell>
  );
}

export function ToolCallBlockView({ block, children }: { block: ToolCallBlock; children?: ReactNode }) {
  const [open, setOpen] = useState(true);
  const [showInput, setShowInput] = useState(false);
  const [showOutput, setShowOutput] = useState(false);

  return (
    <BlockShell variant="outlined" depth={block.depth}>
      <Stack spacing={1}>
        <Row>
          <IconButton size="small" onClick={() => setOpen((v) => !v)} aria-label="toggle tool call">
            {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>

          <Grow>
            <Typography variant="body2" fontWeight={600}>
              Tool: {block.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {block.toolCallId}
            </Typography>
          </Grow>
        </Row>

        <Stack direction="row" spacing={1}>
          {block.input !== undefined ? (
            <Chip
              size="small"
              variant="outlined"
              label={showInput ? 'Hide input' : 'Show input'}
              onClick={() => setShowInput((v) => !v)}
            />
          ) : null}

          {block.output !== undefined ? (
            <Chip
              size="small"
              variant="outlined"
              label={showOutput ? 'Hide output' : 'Show output'}
              onClick={() => setShowOutput((v) => !v)}
            />
          ) : null}
        </Stack>

        <Collapse in={showInput}>
          <Pre>{formatJson(block.input)}</Pre>
        </Collapse>

        <Collapse in={showOutput}>
          <Pre>{formatJson(block.output)}</Pre>
        </Collapse>

        <Collapse in={open}>
          <ChildStack>{children}</ChildStack>
        </Collapse>
      </Stack>
    </BlockShell>
  );
}

export function MessageBlockView({ block }: { block: MessageBlock }) {
  return (
    <BlockShell depth={block.depth}>
      <ChatMarkdown value={block.text} allowHtml={false} />
    </BlockShell>
  );
}

export function ErrorBlockView({ block }: { block: ErrorBlock }) {
  return (
    <BlockShell variant="outlined" depth={block.depth}>
      <Alert severity="error">
        <Stack spacing={1}>
          <Typography variant="body2" fontWeight={600}>
            {block.message}
          </Typography>

          {block.details !== undefined ? <Pre>{formatJson(block.details)}</Pre> : null}
        </Stack>
      </Alert>
    </BlockShell>
  );
}

export function TodoPanel({ todos }: { todos: TodoItem[] }) {
  return (
    <SectionPaper variant="outlined">
      <Stack spacing={1}>
        <Typography variant="subtitle2">Todos</Typography>
        <Divider />

        {todos.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No todos
          </Typography>
        ) : (
          <List dense disablePadding>
            {todos.map((todo) => (
              <TodoItemView key={todo.key} todo={todo} />
            ))}
          </List>
        )}
      </Stack>
    </SectionPaper>
  );
}

export function TodoItemView({ todo }: { todo: TodoItem }) {
  return (
    <ListItem disableGutters>
      <ListItemText
        primary={todo.content}
        secondary={<Chip size="small" label={todo.status} color={getStatusColor(todo.status)} />}
      />
    </ListItem>
  );
}
